import { DingdingOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Form, Input, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type LoginEntryPath,
  authProvider,
  completeDingTalkLogin,
  startDingTalkLogin,
} from '../api/auth-provider.js';
import logoBlue from '../assets/logo-blue.jpg';
import { tx } from '../i18n/translate.js';
import { EmailCodeLoginForm } from './EmailCodeLoginForm.js';
import { LoginParticles } from './LoginParticles.js';
import './LoginPage.css';

type LoginPageProps = {
  mode?: 'dingtalk' | 'password' | 'email-code';
  loginPath?: LoginEntryPath;
  variant?: 'platform' | 'crm' | 'agent' | 'operations' | 'erp';
  passwordRedirectTo?: string;
};

type PasswordLoginValues = {
  username: string;
  password: string;
  rememberPassword?: boolean;
};

const REMEMBERED_PASSWORD_LOGIN_KEY = 'crest_remembered_password_login';

type RememberedPasswordLogin = {
  username: string;
  password: string;
};

export function LoginPage({
  mode = 'dingtalk',
  loginPath = '/login',
  variant = 'platform',
  passwordRedirectTo,
}: LoginPageProps) {
  const navigate = useNavigate();
  const [dingTalkLoading, setDingTalkLoading] = useState(false);
  const [dingTalkError, setDingTalkError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const passwordMode = mode === 'password';
  const emailCodeMode = mode === 'email-code';
  const emailCodePath = loginPath === '/login' ? '/login/email-code' : null;
  const rememberedLogin = passwordMode ? readRememberedPasswordLogin() : null;
  const positioningText =
    variant === 'crm'
      ? tx('legacy:pages.loginpage.crmSalesCenter')
      : variant === 'agent'
        ? 'CREST 智能体中心'
        : variant === 'operations'
          ? 'CREST 运营中心'
      : variant === 'erp'
        ? tx('legacy:pages.loginpage.erpFinanceCenter')
        : tx('legacy:pages.loginpage.agentPlatform');
  const footerText =
    variant === 'crm'
      ? tx('legacy:pages.loginpage.crmFooter')
      : variant === 'agent'
        ? '企业智能体与知识能力入口'
        : variant === 'operations'
          ? '企业运营、报告与审查中心'
      : variant === 'erp'
        ? tx('legacy:pages.loginpage.erpFooter')
        : tx('legacy:pages.loginpage.enterpriseIntel');

  useEffect(() => {
    if (passwordMode || emailCodeMode) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code')?.trim();
    const state = params.get('state')?.trim();
    if (!code || !state) return;

    let active = true;
    setDingTalkLoading(true);
    void completeDingTalkLogin({ code, state }).then((result) => {
      if (!active) return;
      if (result.success) {
        navigate(result.redirectTo ?? '/', { replace: true });
        return;
      }
      setDingTalkError(result.error?.message ?? tx('legacy:pages.loginpage.dingtalkFailed'));
      window.history.replaceState({}, document.title, loginPath);
      setDingTalkLoading(false);
    });
    return () => {
      active = false;
    };
  }, [emailCodeMode, loginPath, navigate, passwordMode]);

  const handleDingTalkLogin = async () => {
    setDingTalkError(null);
    setDingTalkLoading(true);
    try {
      await startDingTalkLogin({ loginEntryPath: loginPath });
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setDingTalkError(
        e?.response?.data?.message ?? e?.message ?? tx('legacy:pages.loginpage.dingtalkFailed'),
      );
      setDingTalkLoading(false);
    }
  };

  const handlePasswordLogin = async (values: PasswordLoginValues) => {
    setPasswordError(null);
    setPasswordLoading(true);
    try {
      const result = await authProvider.login?.({
        username: values.username,
        password: values.password,
      });
      if (result?.success) {
        persistRememberedPasswordLogin(values);
        navigate(passwordRedirectTo ?? result.redirectTo ?? '/', { replace: true });
        return;
      }
      setPasswordError(result?.error?.message ?? tx('hardcoded:api.auth.provider.8f243339'));
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Layout className="crest-login-shell">
      <div className="crest-login-aurora" aria-hidden="true" />
      <LoginParticles />
      <div className="crest-login-beam crest-login-beam-1" aria-hidden="true" />
      <div className="crest-login-beam crest-login-beam-2" aria-hidden="true" />
      <div className="crest-login-halo crest-login-halo-left" aria-hidden="true" />
      <div className="crest-login-halo crest-login-halo-right" aria-hidden="true" />
      <Layout.Content className="crest-login-content">
        <section className="crest-login-card" aria-labelledby="crest-login-title">
          <div className="crest-login-brand">
            <img className="crest-login-logo" src={logoBlue} alt="CREST" />
            <div className="crest-login-positioning">
              <span className="crest-login-agent-node" aria-hidden="true" />
              <span className="crest-login-positioning-text" id="crest-login-title">
                {positioningText}
              </span>
            </div>
          </div>

          {dingTalkError && !passwordMode && !emailCodeMode && (
            <Alert
              className="crest-login-alert"
              type="error"
              message={dingTalkError ?? tx('legacy:pages.loginpage.845973d5')}
              showIcon
              closable
            />
          )}

          {dingTalkLoading && !passwordMode && !emailCodeMode && (
            <Alert
              className="crest-login-alert"
              type="info"
              message={tx('legacy:pages.loginpage.dingtalkPending')}
              showIcon
            />
          )}

          {passwordMode ? (
            <Form
              className="crest-login-password-form"
              layout="vertical"
              initialValues={{
                username: rememberedLogin?.username ?? '',
                password: rememberedLogin?.password ?? '',
                rememberPassword: Boolean(rememberedLogin),
              }}
              onFinish={(values) => void handlePasswordLogin(values as PasswordLoginValues)}
            >
              {passwordError && (
                <Alert
                  className="crest-login-alert"
                  type="error"
                  message={passwordError}
                  showIcon
                  closable
                />
              )}
              <Form.Item
                label="账号"
                name="username"
                rules={[{ required: true, message: '请输入账号' }]}
              >
                <Input autoComplete="username" placeholder="请输入账号" size="large" />
              </Form.Item>
              <Form.Item
                label="密码"
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  autoComplete="current-password"
                  placeholder="请输入密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                className="crest-login-remember-row"
                name="rememberPassword"
                valuePropName="checked"
              >
                <Checkbox>记住密码</Checkbox>
              </Form.Item>
              <Button
                block
                size="large"
                type="primary"
                htmlType="submit"
                className="crest-login-dingtalk-button"
                loading={passwordLoading}
              >
                登录
              </Button>
            </Form>
          ) : emailCodeMode ? (
            <EmailCodeLoginForm backPath="/login" loginPath={loginPath} showBack />
          ) : (
            <>
              <div className="crest-login-action">
                <Button
                  block
                  size="large"
                  type="primary"
                  className="crest-login-dingtalk-button"
                  icon={<DingdingOutlined />}
                  loading={dingTalkLoading}
                  onClick={() => void handleDingTalkLogin()}
                >
                  {tx('legacy:pages.loginpage.dingtalkLogin')}
                </Button>
              </div>
              {emailCodePath && (
                <div className="crest-login-email-entry">
                  <Button
                    type="link"
                    icon={<MailOutlined />}
                    onClick={() => navigate(emailCodePath)}
                  >
                    邮箱验证码登录
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
        <div className="crest-login-footer">{footerText}</div>
      </Layout.Content>
    </Layout>
  );
}

function readRememberedPasswordLogin(): RememberedPasswordLogin | null {
  try {
    const raw = localStorage.getItem(REMEMBERED_PASSWORD_LOGIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RememberedPasswordLogin>;
    if (typeof parsed.username !== 'string' || typeof parsed.password !== 'string') return null;
    return { username: parsed.username, password: parsed.password };
  } catch {
    return null;
  }
}

function persistRememberedPasswordLogin(values: PasswordLoginValues): void {
  try {
    if (!values.rememberPassword) {
      localStorage.removeItem(REMEMBERED_PASSWORD_LOGIN_KEY);
      return;
    }
    localStorage.setItem(
      REMEMBERED_PASSWORD_LOGIN_KEY,
      JSON.stringify({
        username: values.username,
        password: values.password,
      }),
    );
  } catch {
    // localStorage may be unavailable in private or embedded browser contexts.
  }
}
